// Batched gallery assets. The old per-figure GalleryItem class is gone — every
// image is now one instance of a single InstancedBufferGeometry (1 draw call),
// sampled from a DataArrayTexture (sampler2DArray). This module owns:
//   - the GLSL3 shaders (instanced image batch + the single video plane)
//   - buildImageArray(): packs all unique images into one array texture
//   - uvCrop(): cover/contain crop math (per figure, at resize)
// Gallery (./index.js) wires these into meshes and per-frame uniforms.
import * as THREE from 'three';

// ----- shaders (RawShaderMaterial, GLSL3 so we can use sampler2DArray) -----

// Shared vertex body: take a screen-space center, move the whole batch by
// uScroll*uScale, wrap it into one period (infinite tiling, GPU-side), build the
// ortho clip, then bend radially in NDC (the lens). Written twice — once reading
// per-instance attributes, once reading uniforms (the lone video plane).
const VERT_HEAD = /* glsl */ `
	precision highp float;
	uniform mat4 projectionMatrix;
	uniform mat4 modelViewMatrix;
	uniform vec2 uScroll;     // DOM gallery lerped scroll (local units)
	uniform float uScale;     // gallery CSS scale (1 / 1.5 / 2.25)
	uniform vec2 uPeriod;     // tiling period in screen px (gallery w/h, scaled)
	uniform float uLensK;
	in vec3 position;         // plane vertex, xy in [-0.5, 0.5]
	in vec2 uv;
	out vec2 vUv;
`;

const VERT_MAIN = /* glsl */ `
	// place(): center (world px, origin = screen center), size (px), rot, uvR/uvO
	vec4 place(vec2 center, vec2 size, float rot, float tfScale, vec2 uvR, vec2 uvO) {
		vUv = uv * uvR + uvO;
		// +scroll.y in the DOM moves figures down (screen), i.e. -Y in world
		vec2 c = center + vec2(uScroll.x, -uScroll.y) * uScale;
		c = mod(c + uPeriod * 0.5, uPeriod) - uPeriod * 0.5; // wrap, per axis
		vec2 local = position.xy * size * tfScale;
		float s = sin(rot), co = cos(rot);
		local = vec2(local.x * co - local.y * s, local.x * s + local.y * co);
		vec4 clip = projectionMatrix * modelViewMatrix * vec4(c + local, 0.0, 1.0);
		vec2 ndc = clip.xy / clip.w;
		ndc *= 1.0 + uLensK * dot(ndc, ndc);
		clip.xy = ndc * clip.w;
		return clip;
	}
`;

// Instanced image batch.
const IMAGE_VERT = `${VERT_HEAD}
	in vec2 aBase;        // screen-center at scroll=0, minus viewport/2 (world px)
	in vec2 aSize;        // plane px size (cover/contain baked)
	in vec2 aUvRepeat;
	in vec2 aUvOffset;
	in float aLayer;
	in vec2 aOffset;      // tf offset (intro / #debug), px
	in float aRot;        // tf rotation, rad
	in float aTfScale;    // tf scale multiplier
	out float vLayer;
	${VERT_MAIN}
	void main() {
		vLayer = aLayer;
		gl_Position = place(aBase + aOffset, aSize, aRot, aTfScale, aUvRepeat, aUvOffset);
	}
`;

const IMAGE_FRAG = /* glsl */ `
	precision highp float;
	precision highp sampler2DArray;
	uniform sampler2DArray uArray;
	in vec2 vUv;
	in float vLayer;
	out vec4 outColor;
	void main() {
		outColor = texture(uArray, vec3(vUv, vLayer));
	}
`;

// Single video plane — same motion/lens, but its placement comes from uniforms.
const VIDEO_VERT = `${VERT_HEAD}
	uniform vec2 uBase;
	uniform vec2 uSize;
	uniform vec2 uUvRepeat;
	uniform vec2 uUvOffset;
	uniform vec2 uOffset;   // tf offset (intro / #debug), px
	uniform float uRot;     // tf rotation, rad
	uniform float uTfScale; // tf scale multiplier
	${VERT_MAIN}
	void main() {
		gl_Position = place(uBase + uOffset, uSize, uRot, uTfScale, uUvRepeat, uUvOffset);
	}
`;

const VIDEO_FRAG = /* glsl */ `
	precision highp float;
	uniform sampler2D uMap;
	in vec2 vUv;
	out vec4 outColor;
	void main() {
		outColor = texture(uMap, vUv);
	}
`;

export const SHADERS = { IMAGE_VERT, IMAGE_FRAG, VIDEO_VERT, VIDEO_FRAG };

// cover/contain crop, ported from the old GalleryItem.setLayout. Given the plane
// box (w,h) and image aspect, returns the plane's drawn px size + the uv crop
// rect (sample = uv * repeat + offset) into the *image*.
export function uvCrop(mode, w, h, aspect, objectPosition = 'center') {
	const ab = w / h;
	const ai = aspect;
	// crop bias in [0,1]. hx: 0=left .. 1=right. vy: 0=bottom .. 1=top (v increases
	// upward — the layer is v-flipped at pack time). 0.5 each = the old centered crop.
	const pos = String(objectPosition);
	const hx = pos.includes('left') ? 0 : pos.includes('right') ? 1 : 0.5;
	const vy = pos.includes('top') ? 1 : pos.includes('bottom') ? 0 : 0.5;
	if (mode === 'contain') {
		let sw = w;
		let sh = h;
		if (ai > ab) sh = w / ai;
		else sw = h * ai;
		return { width: sw, height: sh, repeat: [1, 1], offset: [0, 0] };
	}
	if (ai > ab) {
		const rx = ab / ai;
		return { width: w, height: h, repeat: [rx, 1], offset: [(1 - rx) * hx, 0] };
	}
	const ry = ai / ab;
	return { width: w, height: h, repeat: [1, ry], offset: [0, (1 - ry) * vy] };
}

// Aspect-preserving fit of an image into one LxL layer: the sub-rect (uv) the
// image occupies, centered, the rest left transparent. Cover/contain crop is
// later composed onto this so the letterbox is never sampled.
function fitRect(aspect) {
	if (aspect >= 1) {
		const fh = 1 / aspect;
		return { repeat: [1, fh], offset: [0, (1 - fh) / 2] };
	}
	const fw = aspect;
	return { repeat: [fw, 1], offset: [(1 - fw) / 2, 0] };
}

// Load + dedupe all image srcs, pack each into one layer of a DataArrayTexture
// (aspect-preserved, v-flipped so data row 0 = uv v 0). Returns the texture plus
// per-src { layer, fit, aspect }. Video srcs are handled separately by Gallery.
export function buildImageArray(srcs, L = 1024) {
	const unique = Array.from(new Set(srcs));
	const canvas = document.createElement('canvas');
	canvas.width = L;
	canvas.height = L;
	const ctx = canvas.getContext('2d');

	const data = new Uint8Array(L * L * 4 * unique.length);
	const info = new Map();

	const loadOne = (src, layer) =>
		new Promise((resolve) => {
			const img = new Image();
			img.onload = () => {
				const aspect = img.naturalWidth / img.naturalHeight || 1;
				const fit = fitRect(aspect);
				ctx.clearRect(0, 0, L, L);
				ctx.save();
				ctx.translate(0, L); // flip vertically: GL expects row 0 at the bottom
				ctx.scale(1, -1);
				ctx.drawImage(
					img,
					fit.offset[0] * L,
					fit.offset[1] * L,
					fit.repeat[0] * L,
					fit.repeat[1] * L
				);
				ctx.restore();
				data.set(ctx.getImageData(0, 0, L, L).data, layer * L * L * 4);
				info.set(src, { layer, fit, aspect });
				resolve();
			};
			img.onerror = () => {
				info.set(src, { layer, fit: fitRect(1), aspect: 1 });
				resolve();
			};
			img.src = src;
		});

	return Promise.all(unique.map((src, i) => loadOne(src, i))).then(() => {
		const tex = new THREE.DataArrayTexture(data, L, L, unique.length);
		tex.format = THREE.RGBAFormat;
		tex.type = THREE.UnsignedByteType;
		tex.colorSpace = THREE.LinearSRGBColorSpace;
		tex.magFilter = THREE.LinearFilter;
		tex.minFilter = THREE.LinearMipmapLinearFilter;
		tex.generateMipmaps = true;
		tex.needsUpdate = true;
		return { texture: tex, info };
	});
}
